import React from 'react';

/**
 * ページネーションコンポーネント
 * 
 * Props:
 * - currentPage: 現在のページ番号 (1-indexed)
 * - totalPages: 総ページ数
 * - totalItems: 総件数
 * - perPage: 1ページあたりの件数
 * - onPageChange: ページ変更時のコールバック (page: number) => void
 */
function Pagination({ currentPage, totalPages, totalItems, perPage, onPageChange }) {
    if (totalPages <= 1) {
        return null;
    }

    const startItem = (currentPage - 1) * perPage + 1;
    const endItem = Math.min(currentPage * perPage, totalItems);

    // 表示するページ番号の範囲を計算
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;

        let start = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let end = Math.min(totalPages, start + maxVisiblePages - 1);

        // 終端に達した場合、開始位置を調整
        if (end - start + 1 < maxVisiblePages) {
            start = Math.max(1, end - maxVisiblePages + 1);
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    };

    const pageNumbers = getPageNumbers();

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 0',
            borderTop: '1px solid #e0e0e0',
            marginTop: '16px'
        }}>
            {/* 件数表示 */}
            <div style={{
                fontSize: '14px',
                color: '#5f6368'
            }}>
                {startItem} - {endItem} / {totalItems} 件
            </div>

            {/* ページナビゲーション */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
            }}>
                {/* 前へボタン */}
                <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    style={{
                        padding: '8px 12px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '6px',
                        backgroundColor: currentPage <= 1 ? '#f5f5f5' : '#fff',
                        color: currentPage <= 1 ? '#bbb' : '#333',
                        cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        fontWeight: '500'
                    }}
                >
                    ◀ 前へ
                </button>

                {/* 最初のページ */}
                {pageNumbers[0] > 1 && (
                    <>
                        <button
                            onClick={() => onPageChange(1)}
                            style={{
                                padding: '8px 12px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '6px',
                                backgroundColor: '#fff',
                                color: '#333',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '500',
                                minWidth: '40px'
                            }}
                        >
                            1
                        </button>
                        {pageNumbers[0] > 2 && (
                            <span style={{ padding: '8px 4px', color: '#999' }}>...</span>
                        )}
                    </>
                )}

                {/* ページ番号 */}
                {pageNumbers.map(page => (
                    <button
                        key={page}
                        onClick={() => onPageChange(page)}
                        style={{
                            padding: '8px 12px',
                            border: currentPage === page ? '1px solid #0176d3' : '1px solid #e0e0e0',
                            borderRadius: '6px',
                            backgroundColor: currentPage === page ? '#0176d3' : '#fff',
                            color: currentPage === page ? '#fff' : '#333',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: currentPage === page ? '700' : '500',
                            minWidth: '40px'
                        }}
                    >
                        {page}
                    </button>
                ))}

                {/* 最後のページ */}
                {pageNumbers[pageNumbers.length - 1] < totalPages && (
                    <>
                        {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                            <span style={{ padding: '8px 4px', color: '#999' }}>...</span>
                        )}
                        <button
                            onClick={() => onPageChange(totalPages)}
                            style={{
                                padding: '8px 12px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '6px',
                                backgroundColor: '#fff',
                                color: '#333',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '500',
                                minWidth: '40px'
                            }}
                        >
                            {totalPages}
                        </button>
                    </>
                )}

                {/* 次へボタン */}
                <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    style={{
                        padding: '8px 12px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '6px',
                        backgroundColor: currentPage >= totalPages ? '#f5f5f5' : '#fff',
                        color: currentPage >= totalPages ? '#bbb' : '#333',
                        cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        fontWeight: '500'
                    }}
                >
                    次へ ▶
                </button>
            </div>
        </div>
    );
}

export default Pagination;
